import { AddIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Select,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AwardCriterionType,
  type AwardCriteriaDto,
  type AwardCriterionDto,
  LibraryElementTypeDto,
  type LibraryElementContentDto,
  type PriceModelDto,
} from "../../autogen/bff-api";
import { SelectableCard } from "../../common/input/SelectableCard";
import { fromLibraryCriterion } from "../../common/library/awardLibraryContent";
import { useConfirmationModal } from "../../common/useConfirmationModal";
import { WeightSummary } from "../sourcing-events/buyer/wizard/components/WeightSummary";
import {
  DEFAULT_BREAKPOINT_PERCENT,
  PRICE_MODEL_OPTIONS,
  type PriceModelKey,
  priceModelKey,
} from "../sourcing-events/common/priceModel";
import { AwardCriterionList } from "../kravbibliotek/AwardCriterionList";
import { AwardCriterionModal, type AwardCriterionFormValues } from "../kravbibliotek/AwardCriterionModal";
import { AddedFromLibraryBanner } from "../../common/library/AddedFromLibraryBanner";
import { AddRequirementPanel } from "../../common/library/AddRequirementPanel";
import { useAddFromLibrary } from "../../common/library/useAddFromLibrary";
import { useProcurementTerms } from "../../common/terminology/useProcurementTerms";
import { TemplateSectionCard } from "./TemplateSectionCard";

// t('Lowest price') t('Award based solely on price.') t('Best price-quality ratio')
// t("No award model chosen. Applying the template leaves the procurement's award model untouched.")
// t('Price model') t('Proportional method') t('Linear method') t('Hybrid model') t('Breakpoint')
// t('New criterion') t('Choose from the library') t('Add award criteria') t('award criteria')
// t('Delete criterion') t('Are you sure you want to delete this criterion?') t('Delete')

// Both § 5b "green paths" are listed: an event can weight the environment either as its own criterion
// or as a specification, and a template saved from such an event must be able to round-trip the choice.
// Without the specification variant its criteria render but open with an empty type select, which the
// modal then refuses to submit — an uneditable dead end.
const MAL_AWARD_TYPES: AwardCriterionType[] = [
  AwardCriterionType.Price,
  AwardCriterionType.Cost,
  AwardCriterionType.Quality,
  AwardCriterionType.QualityEnvironmentalCriteria,
  AwardCriterionType.QualityEnvironmentalSpecification,
];

const PRICE_MODEL_LABEL: Record<PriceModelKey, string> = {
  proportional: "Proportional method",
  linear: "Linear method",
  hybrid: "Hybrid model",
};

type Model = "lowestPrice" | "bestPriceQualityRatio";

interface Props {
  awardCriteria: AwardCriteriaDto | null | undefined;
  priceModel: PriceModelDto | null | undefined;
  onChangeAward: (v: AwardCriteriaDto | undefined) => void;
  onChangePriceModel: (v: PriceModelDto | undefined) => void;
}

type ModalState = { mode: "edit"; index: number; initialValues: AwardCriterionFormValues };

export const TemplateAwardSection = ({ awardCriteria, priceModel, onChangeAward, onChangePriceModel }: Props) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const confirmationModal = useConfirmationModal();
  const [modal, setModal] = useState<ModalState | null>(null);
  const [modalKey, setModalKey] = useState(0);

  // No award model is a real, persisted state — a template may cover only minimum and qualification
  // requirements, and then it must not prescribe an award model for the procurements it is applied to.
  const model: Model | null = awardCriteria == null ? null : awardCriteria.bestPriceQualityRatio ? "bestPriceQualityRatio" : "lowestPrice";
  const criteria = awardCriteria?.bestPriceQualityRatio?.criteria ?? [];
  const pmKey = priceModelKey(priceModel);

  const selectModel = (m: Model) => {
    if (m === "lowestPrice") {
      onChangeAward({ lowestPrice: { selected: true }, bestPriceQualityRatio: null });
      onChangePriceModel(undefined);
    } else {
      onChangeAward({ lowestPrice: null, bestPriceQualityRatio: { criteria } });
      if (!priceModel) onChangePriceModel({ proportional: { selected: true }, linear: null, hybrid: null });
    }
  };

  const clearModel = () => {
    onChangeAward(undefined);
    onChangePriceModel(undefined);
  };

  const setPriceModelKey = (key: PriceModelKey) => {
    onChangePriceModel({
      proportional: key === "proportional" ? { selected: true } : null,
      linear: key === "linear" ? { selected: true } : null,
      hybrid:
        key === "hybrid"
          ? { breakpointPercent: priceModel?.hybrid?.breakpointPercent ?? DEFAULT_BREAKPOINT_PERCENT }
          : null,
    });
  };

  const setCriteria = (next: AwardCriterionDto[]) => {
    onChangeAward({
      lowestPrice: null,
      bestPriceQualityRatio: { ...(awardCriteria?.bestPriceQualityRatio ?? {}), criteria: next },
    });
  };


  const openEdit = (index: number) => {
    const c = criteria[index];
    setModalKey((k) => k + 1);
    setModal({
      mode: "edit",
      index,
      initialValues: {
        type: c.type,
        name: c.name ?? "",
        description: c.description,
        weight: c.weight?.number != null ? String(c.weight.number) : "",
      },
    });
  };

  const handleSubmit = async (values: AwardCriterionDto) => {
    if (modal) library.save(criteria.map((c, i) => (i === modal.index ? values : c)));
    setModal(null);
  };

  const handleDelete = (index: number) => {
    confirmationModal.show({
      title: terms.deleteCriterion,
      description: terms.deleteCriterionConfirm,
      confirmButtonText: t("Delete") ?? "",
      confirmButtonColor: "red",
      onConfirmed: () => library.save(criteria.filter((_, i) => i !== index)),
    });
  };

  const library = useAddFromLibrary({ items: criteria, commit: setCriteria });

  const addFromLibrary = (contents: LibraryElementContentDto[]) =>
    library.add(contents.flatMap((c) => (c.award ? [fromLibraryCriterion(c.award)] : [])));

  return (
    <TemplateSectionCard
      title={terms.awardCriteria}
      count={model === "bestPriceQualityRatio" ? criteria.length : 0}
      description={terms.chooseAwardModelHint}
    >
      <VStack align="stretch" spacing="4">
        <SelectableCard
          isSelected={model === "lowestPrice"}
          type="button"
          w="full"
          onClick={() => selectModel("lowestPrice")}
        >
          <Box textAlign="left">
            <Text fontWeight="bold">{t("Lowest price")}</Text>
            <Text fontSize="sm" color="smMuted">
              {t("Award based solely on price.")}
            </Text>
          </Box>
        </SelectableCard>
        <SelectableCard
          isSelected={model === "bestPriceQualityRatio"}
          type="button"
          w="full"
          onClick={() => selectModel("bestPriceQualityRatio")}
        >
          <Box textAlign="left">
            <Text fontWeight="bold">{t("Best price-quality ratio")}</Text>
            <Text fontSize="sm" color="smMuted">
              {terms.weightedAwardCriteriaHint}
            </Text>
          </Box>
        </SelectableCard>

        {model !== null && (
          <Button variant="link" size="sm" colorScheme="teal" onClick={clearModel} alignSelf="flex-start">
            {terms.clearAwardModel}
          </Button>
        )}

        {model === null ? (
          <Alert status="info" borderRadius="lg">
            <AlertIcon />
            <Text fontSize="sm">
              {terms.noAwardModelChosenHint}
            </Text>
          </Alert>
        ) : model === "lowestPrice" ? (
          <Alert status="info" borderRadius="lg">
            <AlertIcon />
            <Text fontSize="sm">{terms.lowestPriceHasNoAwardCriteria}</Text>
          </Alert>
        ) : (
          <>
            <FormControl>
              <FormLabel>{t("Price model")}</FormLabel>
              <Select value={pmKey} onChange={(e) => setPriceModelKey(e.target.value as PriceModelKey)}>
                {PRICE_MODEL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {t(PRICE_MODEL_LABEL[o.value])}
                  </option>
                ))}
              </Select>
            </FormControl>
            {pmKey === "hybrid" && (
              <FormControl maxW="48">
                <FormLabel>{t("Breakpoint")}</FormLabel>
                <InputGroup>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={priceModel?.hybrid?.breakpointPercent ?? DEFAULT_BREAKPOINT_PERCENT}
                    onChange={(e) =>
                      onChangePriceModel({ proportional: null, linear: null, hybrid: { breakpointPercent: Number(e.target.value) } })
                    }
                  />
                  <InputRightAddon>%</InputRightAddon>
                </InputGroup>
              </FormControl>
            )}

            {library.lastAdded && <AddedFromLibraryBanner count={library.lastAdded.count} onUndo={library.undo} />}
            <AwardCriterionList
              items={criteria}
              onAdd={() => library.openPanel("library")}
              onEdit={openEdit}
              onDelete={handleDelete}
              markedCount={library.markedCount}
            />
            {criteria.length > 0 && <WeightSummary criteria={criteria} />}
            {criteria.length > 0 && (
              <Flex gap="3" alignSelf="flex-start">
                <Button variant="outline" size="sm" leftIcon={<AddIcon />} onClick={() => library.openPanel("new")}>
                  {terms.newCriterion}
                </Button>
                <Button colorScheme="teal" size="sm" onClick={() => library.openPanel("library")}>
                  {t("Choose from the library")}
                </Button>
              </Flex>
            )}
          </>
        )}
      </VStack>

      <AddRequirementPanel
        isOpen={library.isPanelOpen}
        onClose={library.closePanel}
        type={LibraryElementTypeDto.Award}
        title={terms.addAwardCriteria}
        typeNoun={terms.awardCriteriaNoun}
        isAlreadyInSection={(el) => !!el.content.award?.name && criteria.some((c) => c.name === el.content.award?.name)}
        initialTab={library.tab}
        awardTypes={MAL_AWARD_TYPES}
        onAdd={addFromLibrary}
      />

      <AwardCriterionModal
        key={modalKey}
        isOpen={modal !== null}
        isSaving={false}
        mode="edit"
        allowedTypes={MAL_AWARD_TYPES}
        defaultValues={modal?.initialValues}
        subCriteria={modal ? criteria[modal.index]?.subCriteria : undefined}
        onSubmit={handleSubmit}
        onClose={() => setModal(null)}
      />
      {confirmationModal.modal}
    </TemplateSectionCard>
  );
};
